import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  SafeAreaView,
  Alert,
  ActivityIndicator,
  Platform,
  Linking,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../screens/ThemeContext';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';
import { useAuth } from '../private/AuthContext';
import { useFocusEffect } from '@react-navigation/native';
import PurchaseService from '../services/PurchaseService';

const StatusBar = Platform.OS === 'web' ? () => null : require('react-native').StatusBar;
const isWeb = typeof window !== 'undefined';

export default function HistoriqueDetailScreen({ route, navigation }) {
  const { document } = route.params;
  const { theme } = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState({});
  const [documentLoading, setDocumentLoading] = useState(true);
  const [groupedDocuments, setGroupedDocuments] = useState({});
  const [ungroupedDocuments, setUngroupedDocuments] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  // Pour suivre si des achats ont été effectués
  const [purchasesMade, setPurchasesMade] = useState(0);
  
  // Utiliser useFocusEffect pour recharger les données quand l'écran devient actif
  useFocusEffect(
    useCallback(() => {
      if (document.type === 'concours') {
        fetchAllConcoursDocuments();
      } else {
        setDocumentLoading(false);
      }
    }, [document, purchasesMade]) // Réagir aux changements de document et aux achats
  );

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const fetchAllConcoursDocuments = async () => {
    try {
      setDocumentLoading(true);
      const concoursRef = doc(db, 'concours', document.id);
      const concoursDoc = await getDoc(concoursRef);
      
      if (concoursDoc.exists()) {
        const concoursData = concoursDoc.data();
        
        // Pour les documents non groupés
        let ungrouped = [];
        
        // Si nous avons des documents non groupés
        if (concoursData.documents && !concoursData.hasGroups) {
          // Filtrer pour ne garder que les documents achetés
          ungrouped = concoursData.documents.filter(doc => 
            isDocumentPurchased(doc, document.purchasedEpreuves)
          );
        }
        
        // Pour les documents groupés
        let grouped = {};
        
        // Si nous avons des groupes
        if (concoursData.hasGroups && concoursData.groups) {
          concoursData.groups.forEach(group => {
            if (group.documents) {
              // Filtrer les documents achetés dans ce groupe
              const purchasedInGroup = group.documents.filter(doc => 
                isDocumentPurchased({...doc, groupId: group.id}, document.purchasedEpreuves)
              );
              
              // Seulement ajouter le groupe s'il contient des documents achetés
              if (purchasedInGroup.length > 0) {
                grouped[group.id] = {
                  nom: group.nom,
                  documents: purchasedInGroup.map(doc => ({...doc, groupId: group.id}))
                };
              }
            }
          });
        }
        
        setUngroupedDocuments(ungrouped);
        setGroupedDocuments(grouped);
        
        // Auto-expand le premier groupe s'il y en a un
        const groupIds = Object.keys(grouped);
        if (groupIds.length > 0) {
          setExpandedGroups({[groupIds[0]]: true});
        }
        
        
      }
    } catch (error) {
      console.error('Error fetching concours documents:', error);
      Alert.alert('Erreur', 'Impossible de récupérer les documents');
    } finally {
      setDocumentLoading(false);
    }
  };

  const isDocumentPurchased = (doc, purchasedEpreuves) => {
    const isPurchased = purchasedEpreuves.some(purchased => {
      const match = purchased.titre === doc.titre && 
                   purchased.annee.toString() === doc.annee.toString() && 
                   purchased.filiere === doc.filiere &&
                   (doc.groupId ? purchased.groupId === doc.groupId : true);
                   
      return match;
    });
    
    return isPurchased || doc.gratuit === true;
  };

  // Fonction d'alerte compatible web/mobile
  const showAlert = (title, message) => {
    if (isWeb) {
      window.alert(`${title}\n\n${message}`);
    } else {
      Alert.alert(title, message);
    }
  };

  // Nouvelle fonction pour afficher l'alerte de restriction sur web
  const showRestrictionAlert = () => {
    showAlert(
      '📱 Application Mobile Requise',
      'Ce document est protégé et ne peut être consulté que sur l\'application mobile CD Connect.\n\nTéléchargez l\'application pour accéder à ce contenu.'
    );
  };

  const handleDocumentOpen = async (doc) => {
    try {
      // Définir un identifiant unique pour cette opération de chargement
      const loadingId = `${doc.titre}-${doc.annee}-${doc.filiere}${doc.groupId ? `-${doc.groupId}` : ''}`;
      setLoading(prev => ({ ...prev, [loadingId]: true }));

      // ✅ Vérifier d'abord la restriction sur web
      if (isWeb && document.restrictionTelechargement) {
        showRestrictionAlert();
        setLoading(prev => ({ ...prev, [loadingId]: false }));
        return;
      }

      // 1. Récupérer l'URL en temps réel depuis Firestore via PurchaseService
      let docUrl = null;

      if (document.type === 'concours') {
        docUrl = await PurchaseService.getDocumentUrl(document.id, 'concours', {
          titre: doc.titre,
          annee: doc.annee,
          filiere: doc.filiere,
          groupId: doc.groupId,
          // Identité stable + auto-réparation (voir documentIdentity.js)
          epreuveId: doc.epreuveId,
          purchaseId: doc.purchaseId,
        });
      } else if (document.type === 'bords') {
        docUrl = await PurchaseService.getDocumentUrl(document.id, 'bords');
      }

      // 2. Fallback : URL depuis le document local
      if (!docUrl && doc.url) {
        docUrl = doc.url;
        console.log("Fallback URL depuis le document local:", docUrl);
      }

      // 3. Fallback : chercher dans les épreuves achetées
      if (!docUrl && document.type === 'concours') {
        const purchasedDoc = document.purchasedEpreuves.find(purchased =>
          purchased.titre === doc.titre &&
          purchased.annee.toString() === doc.annee.toString() &&
          purchased.filiere === doc.filiere &&
          (doc.groupId ? purchased.groupId === doc.groupId : true)
        );

        if (purchasedDoc && purchasedDoc.url) {
          docUrl = purchasedDoc.url;
          console.log("Fallback URL depuis les achats:", docUrl);
        }
      }

      if (!docUrl) {
        console.error('URL manquante pour:', doc);
        throw new Error('URL du document manquante');
      }

      // Ouvrir l'URL
      console.log("Ouverture de l'URL temps réel:", docUrl);
      await Linking.openURL(docUrl);
    } catch (error) {
      console.error('Error opening document:', error);
      showAlert('Erreur', 'Impossible d\'ouvrir le document: ' + error.message);
    } finally {
      const loadingId = `${doc.titre}-${doc.annee}-${doc.filiere}${doc.groupId ? `-${doc.groupId}` : ''}`;
      setLoading(prev => ({ ...prev, [loadingId]: false }));
    }
  };

  const handlePurchase = (doc) => {
    // Naviguer vers l'écran de paiement avec le callback pour actualiser après achat
    navigation.navigate('PaymentScreen', {
      document: document,
      documentDetail: doc,
      onPurchaseComplete: (newPurchase) => {
        console.log("Achat réussi, mise à jour des documents affichés");
        
        // Ajouter aux épreuves achetées
        document.purchasedEpreuves.push(newPurchase);
        
        // Incrémenter le compteur pour déclencher l'actualisation
        setPurchasesMade(prev => prev + 1);
      }
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp.seconds * 1000).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderGroupedDocuments = () => {
    return Object.entries(groupedDocuments).map(([groupId, group]) => (
      <View key={groupId} style={styles.groupContainer}>
        <TouchableOpacity 
          style={[styles.groupHeader, { 
            backgroundColor: theme.surface,
            borderColor: theme.border 
          }]}
          onPress={() => toggleGroup(groupId)}
        >
          <View style={styles.groupTitleContainer}>
            <MaterialCommunityIcons 
              name="account-group" 
              size={24} 
              color={theme.primary} 
            />
            <Text style={[styles.groupTitle, { color: theme.text }]}>
              {group.nom}
            </Text>
          </View>
          <MaterialCommunityIcons 
            name={expandedGroups[groupId] ? "chevron-up" : "chevron-down"} 
            size={24} 
            color={theme.text} 
          />
        </TouchableOpacity>

        {expandedGroups[groupId] && group.documents.map((doc, index) => 
          renderDocumentItem(doc, index)
        )}
      </View>
    ));
  };

  const renderDocumentItem = (doc, index) => {
    const loadingId = `${doc.titre}-${doc.annee}-${doc.filiere}${doc.groupId ? `-${doc.groupId}` : ''}`;
    const isDownloading = loading[loadingId];
    const isPurchased = document.type === 'bords' ? true : isDocumentPurchased(doc, document.purchasedEpreuves);
    const isRestricted = isWeb && document.restrictionTelechargement;

    return (
      <View
        key={index}
        style={[styles.documentItem, { 
          backgroundColor: theme.surface, 
          borderColor: theme.border,
          marginLeft: doc.groupId ? 20 : 0
        }]}
      >
        <View style={styles.documentIcon}>
          <MaterialCommunityIcons 
            name={isRestricted ? "cellphone-lock" : "file-pdf-box"}
            size={24} 
            color={isRestricted ? theme.danger : theme.primary}
          />
        </View>
        <View style={styles.documentInfo}>
          <Text style={[styles.documentTitle, { color: theme.text }]}>
            {`${doc.titre} - ${doc.annee}`}
          </Text>
          <Text style={[styles.documentMeta, { color: theme.textSecondary }]}>
            {`${doc.nombrePages} pages • ${doc.filiere}`}
          </Text>
          {isRestricted && (
            <Text style={[styles.restrictionText, { color: theme.danger }]}>
              📱 Application mobile requise
            </Text>
          )}
          <Text style={[styles.documentPrice, { color: theme.primary }]}>
            {doc.gratuit ? 'Gratuit' : `${doc.prix} FCFA`}
          </Text>
        </View>
        <TouchableOpacity
          style={[styles.downloadButton, { 
            backgroundColor: isPurchased ? theme.primary + '20' : theme.danger + '20' 
          }]}
          onPress={() => {
            if (isPurchased) {
              handleDocumentOpen(doc);
            } else {
              handlePurchase(doc);
            }
          }}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <ActivityIndicator size="small" color={theme.primary} />
          ) : (
            <MaterialCommunityIcons 
              name={isPurchased ? "download" : "lock"}
              size={24} 
              color={isPurchased ? theme.primary : theme.danger}
            />
          )}
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.primary }]}>
      <StatusBar barStyle="light-content" backgroundColor={theme.primary} />
      <ScrollView style={[styles.container, { backgroundColor: theme.background }]}>
        <Image
          source={{ uri: document.type === 'concours' ? document.coverImage : document.image }}
          style={styles.coverImage}
        />
        
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#fff" />
        </TouchableOpacity>

        <View style={styles.content}>
          <View style={[styles.infoCard, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <Text style={[styles.documentTitle, { color: theme.text }]}>
              {document.type === 'concours' ? 
                (document.sigle ? `${document.nomComplet} (${document.sigle})` : document.nomComplet) : 
                document.titre}
            </Text>

            <View style={styles.metaInfo}>
              <View style={[styles.badge, { backgroundColor: theme.primary + '20' }]}>
                <MaterialCommunityIcons
                  name={document.type === 'concours' ? 'school' : 'book-open-variant'}
                  size={16}
                  color={theme.primary}
                />
                <Text style={[styles.badgeText, { color: theme.primary }]}>
                  {document.type === 'concours' ? 'Concours' : 'Bord'}
                </Text>
              </View>
              {/* Badge de restriction */}
              {isWeb && document.restrictionTelechargement && (
                <View style={[styles.badge, { backgroundColor: theme.danger + '20', marginLeft: 8 }]}>
                  <MaterialCommunityIcons
                    name="cellphone"
                    size={16}
                    color={theme.danger}
                  />
                  <Text style={[styles.badgeText, { color: theme.danger }]}>
                    App Mobile Requise
                  </Text>
                </View>
              )}
            </View>

            {document.type === 'bords' && (
              <View style={styles.statsContainer}>
                <View style={styles.statItem}>
                  <MaterialCommunityIcons name="translate" size={24} color={theme.primary} />
                  <Text style={[styles.statValue, { color: theme.text }]}>
                    {document.langue === 'fr' ? 'Français' : 'Anglais'}
                  </Text>
                  <Text style={[styles.statLabel, { color: theme.textSecondary }]}>Langue</Text>
                </View>
                <View style={styles.statItem}>
                  <MaterialCommunityIcons name="file-document" size={24} color={theme.primary} />
                  <Text style={[styles.statValue, { color: theme.text }]}>
                    {document.nombrePages}
                  </Text>
                  <Text style={[styles.statLabel, { color: theme.textSecondary }]}>Pages</Text>
                </View>
                <View style={styles.statItem}>
                  <MaterialCommunityIcons name="currency-usd" size={24} color={theme.primary} />
                  <Text style={[styles.statValue, { color: theme.text }]}>
                    {document.prix}
                  </Text>
                  <Text style={[styles.statLabel, { color: theme.textSecondary }]}>Prix FCFA</Text>
                </View>
              </View>
            )}

            <View style={styles.separator} />

            <Text style={[styles.sectionTitle, { color: theme.text }]}>Description</Text>
            <Text style={[styles.description, { color: theme.textSecondary }]}>
              {document.description}
            </Text>
          </View>

          <View style={[styles.documentsSection, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <Text style={[styles.sectionTitle, { color: theme.text }]}>
              {document.type === 'concours' ? 'Documents disponibles' : 'Document'}
            </Text>
            
            {documentLoading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={theme.primary} />
                <Text style={[styles.loadingText, { color: theme.textSecondary }]}>
                  Chargement des documents...
                </Text>
              </View>
            ) : document.type === 'concours' ? (
              <>
                {/* Afficher les documents non groupés s'il y en a */}
                {ungroupedDocuments.length > 0 && (
                  ungroupedDocuments.map((doc, index) => renderDocumentItem(doc, index))
                )}
                
                {/* Afficher les documents groupés s'il y en a */}
                {Object.keys(groupedDocuments).length > 0 && renderGroupedDocuments()}
                
                {/* Message si aucun document trouvé */}
                {ungroupedDocuments.length === 0 && Object.keys(groupedDocuments).length === 0 && (
                  <Text style={[styles.emptyMessage, { color: theme.textSecondary }]}>
                    Aucun document acheté trouvé
                  </Text>
                )}
              </>
            ) : (
              renderDocumentItem(document, 0)
            )}
          </View>
        </View>
        <View style={styles.bottomSpace} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: { 
    flex: 1,
  },
  coverImage: {
    width: '100%',
    height: 250,
  },
  backButton: {
    position: 'absolute',
    top: 20,
    left: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: 10,
    padding: 8,
  },
  content: {
    padding: 20,
    marginTop: -40,
  },
  infoCard: {
    padding: 20,
    borderRadius: 20,
    borderWidth: 1,
    marginBottom: 20,
  },
  documentTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  metaInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 20,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  badgeText: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 20,
    paddingVertical: 20,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 8,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
  },
  separator: {
    height: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    marginVertical: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  description: {
    fontSize: 14,
    lineHeight: 22,
  },
  documentsSection: {
    padding: 20,
    borderRadius: 20,
    borderWidth: 1,
  },
  documentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
  },
  documentIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  documentInfo: {
    flex: 1,
  },
  documentTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  documentMeta: {
    fontSize: 14,
    marginBottom: 4,
  },
  restrictionText: {
    fontSize: 13,
    fontWeight: '600',
    marginTop: 4,
    marginBottom: 4,
  },
  documentPrice: {
    fontSize: 14,
    fontWeight: '600',
  },
  downloadButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  bottomSpace: {
    height: 40,
  },
  loadingContainer: {
    padding: 30,
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
  },
  emptyMessage: {
    textAlign: 'center',
    padding: 20,
    fontStyle: 'italic',
  },
  groupContainer: {
    marginBottom: 16,
  },
  groupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderWidth: 1,
    borderRadius: 12,
    marginBottom: 8,
  },
  groupTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  groupTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 12,
  },
});
//web